import { useSyncExternalStore } from "react";
import { getToken } from "../services/api";

export type Customer = { _id: string; phone: string; name?: string; email?: string };

export type AuthState = {
  token: string | null;
  customer: Customer | null;
  isLoggedIn: boolean;
  loaded: boolean;
};

const TOKEN_KEY = "sell_token";
const CUSTOMER_KEY = "sell_customer";

function loadInitial(): AuthState {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(CUSTOMER_KEY);
    const customer = raw ? JSON.parse(raw) : null;
    if (token && customer) return { token, customer, isLoggedIn: true, loaded: true };
    if (token) return { token, customer: null, isLoggedIn: false, loaded: false };
  } catch { /* ignore */ }
  return { token: null, customer: null, isLoggedIn: false, loaded: false };
}

let state: AuthState = loadInitial();
const listeners = new Set<() => void>();
function notify() { listeners.forEach(l => l()); }
function set(patch: Partial<AuthState>) {
  state = { ...state, ...patch };
  try {
    if (state.token) localStorage.setItem(TOKEN_KEY, state.token);
    else localStorage.removeItem(TOKEN_KEY);
    if (state.customer) localStorage.setItem(CUSTOMER_KEY, JSON.stringify(state.customer));
    else localStorage.removeItem(CUSTOMER_KEY);
  } catch { /* ignore */ }
  notify();
}

export const authStore = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },

  login: (token: string, customer: Customer) => {
    set({ token, customer, isLoggedIn: true, loaded: true });
  },

  setCustomer: (customer: Customer) => {
    set({ customer, isLoggedIn: true, loaded: true });
  },

  logout: () => {
    set({ token: null, customer: null, isLoggedIn: false, loaded: true });
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_KEY);
  },

  ensureLoaded: async () => {
    if (state.loaded) return;
    const token = getToken();
    if (!token) { set({ loaded: true }); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/customer/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.customer) {
        set({ token, customer: data.customer, isLoggedIn: true, loaded: true });
      } else {
        set({ token: null, customer: null, isLoggedIn: false, loaded: true });
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(CUSTOMER_KEY);
      }
    } catch {
      set({ loaded: true });
    }
  },
};

export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.get, authStore.get);
}
